import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Loader2, Mail, MapPin, PhoneCall, User, X } from 'lucide-react';
import { apiGet, apiPost } from '../../lib/api.js';
import { fmtPhone } from '../../lib/format.js';
import { useLead } from '../../hooks/useLead.js';
import LeadDetailCenter from './LeadDetailCenter.jsx';
import ScheduleAppointmentModal from '../ScheduleAppointmentModal.jsx';
import useBookEvent from '../../hooks/useBookEvent.js';

/**
 * Self-contained lead details: header (name, phone, email, state, disposition)
 * plus every Lead Detail tab (Activity, SMS, Calls, Email, Calendar with Google
 * sync + conflicts, Scripts, Documents, ...). Used beside the voicemail list and
 * inside the phone panel's View Lead drawer.
 * @param {Object} props
 * @param {string|null} props.leadId
 * @param {() => void} [props.onClose] - Shows an X when provided.
 * @param {boolean} [props.hideHeader] - Skip the header (host supplies its own).
 * @returns {JSX.Element}
 */
export default function LeadDetailsPane({ leadId, onClose, hideHeader = false }) {
  const qc = useQueryClient();
  const [scheduling, setScheduling] = useState(false);
  const { lead, messages, calls, emailMessages, isLoading, error, refetch } = useLead(leadId, { refetchInterval: 15_000 });

  const { data: dispData } = useQuery({
    queryKey: ['dispositions'],
    queryFn: () => apiGet('/dispositions'),
    staleTime: 60_000,
    enabled: !!leadId && !hideHeader,
  });
  const tags = (lead?.dispositionTags || [])
    .map((slug) => (dispData?.dispositions || []).find((d) => d.slug === slug))
    .filter(Boolean);

  function changed() {
    refetch();
    ['leads', 'lead-activity-log', 'dial-queue'].forEach((k) => qc.invalidateQueries({ queryKey: k === 'lead-activity-log' ? [k, leadId] : [k] }));
  }

  const book = useBookEvent(leadId, () => { setScheduling(false); changed(); });

  const name = [lead?.firstName, lead?.lastName].filter(Boolean).join(' ');
  const place = [lead?.city, lead?.state].filter(Boolean).join(', ');

  return (
    <div className="h-full min-h-0 flex flex-col bg-civix-panel" data-testid="lead-details-pane">
      {!hideHeader && (
        <div className="px-4 py-3 bg-civix-purple text-white flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2 font-display font-bold truncate"><User className="w-4 h-4 shrink-0" />{name || 'Lead'}</div>
            {lead && (
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-white/85">
                {lead.phone && <span className="inline-flex items-center gap-1"><PhoneCall className="w-3 h-3" />{fmtPhone(lead.phone)}</span>}
                {lead.email && <span className="inline-flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{lead.email}</span>}
                {place && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{place}</span>}
              </div>
            )}
            {tags.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1">
                {tags.map((d) => <span key={d.slug} className="text-[10px] rounded-sm px-1.5 py-0.5 bg-white/20">{d.label}</span>)}
              </div>
            )}
          </div>
          {onClose && (
            <button onClick={onClose} className="text-white/80 hover:text-white shrink-0" aria-label="Close lead details"><X className="w-5 h-5" /></button>
          )}
        </div>
      )}

      {!leadId ? (
        <div className="flex-1 flex items-center justify-center p-8 text-sm text-white/50 text-center">
          No lead selected. Select or dial a lead to view their information.
        </div>
      ) : isLoading && !lead ? (
        <div className="flex-1 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-civix-purple" /></div>
      ) : !lead ? (
        <div className="flex-1 flex items-center justify-center p-8 text-sm text-white/50 text-center">
          {error?.response?.status === 403 ? 'You do not have access to this lead.' : 'This lead could not be loaded.'}
        </div>
      ) : (
        <div className="flex-1 min-h-0 p-3">
          <LeadDetailCenter
            leadId={leadId}
            lead={lead}
            messages={messages}
            calls={calls}
            emailMessages={emailMessages}
            onChange={changed}
            onCreateAppointment={() => setScheduling(true)}
          />
        </div>
      )}

      {scheduling && lead && <ScheduleAppointmentModal lead={lead} onCancel={() => setScheduling(false)} onSubmit={book} />}
    </div>
  );
}
